import {
  SortableTableHeaderCell,
  Table,
  TableBody,
  TableCell,
  TableRow,
  makeStyles,
  TableContainer,
  TableHead,
  Tooltip,
  Checkbox,
  TextField,
  Theme,
} from '@bb-ui/react-library';
import { useTranslation } from 'react-i18next';
import { MassQueryToolingRow } from './MassQueryToolingRow';
import { LearnInstanceItem } from './MassQueryTooling.types';
import * as React from 'react';
import { SortDirection } from '@bb-ui/react-library/dist/components/SortableTable/SortableTable.types';
import { Pagination } from 'components/Pagination';
import { ClientData } from 'App.types';

export interface MassQueryToolingTableProps {
  sites: LearnInstanceItem[];
  clientsMap?: Map<string, ClientData>;
  selectedTenantIds: Set<string>;
  onToggleSite: (site: LearnInstanceItem) => void;
  onToggleAll: () => void;
}

const ITEMS_PER_PAGE = [3, 5, 10, 25, 50, 100];

const useStyles = makeStyles((theme: Theme) => ({
  tableContainer: {
    width: '100%',
    overflowX: 'auto',
    marginBottom: theme.spacing(3),
    flexShrink: 0,
  },
  table: {
    width: '100%',
    minWidth: '800px',
    tableLayout: 'fixed',
    '& th': {
      padding: theme.spacing(1.5),
      borderBottom: `1px solid ${theme.palette.grey[300]}`,
      textAlign: 'center',
      whiteSpace: 'nowrap',
    },
    '& td': {
      padding: theme.spacing(1.5),
    },
    '& thead th': {
      backgroundColor: theme.palette.grey[300],
      fontWeight: 600,
      color: theme.palette.text.primary,
    },
    '& thead th:first-child, & tbody td:first-child': {
      width: '140px',
      minWidth: '140px',
      textAlign: 'center',
    },
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    '& #mass-query-tooling-items-per-page': {
      backgroundColor: theme.palette.common.white,
      borderRadius: theme.shape.borderRadius,
    },
    '& #mass-query-tooling-items-per-page select': {
      backgroundColor: theme.palette.common.white,
    },
  },
  textField: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  label: {
    marginBottom: 0,
    marginRight: theme.spacing(1),
    fontWeight: theme.typography.fontWeightRegular,
  },
  pagination: {
    justifyContent: 'center',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    '& #mass-query-tooling-pagination': {
      backgroundColor: theme.palette.common.white,
      borderRadius: theme.shape.borderRadius,
    },
  },
  selectHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
  },
}));

const getRowId = (site: LearnInstanceItem) =>
  site.InstallationId || site.Url || String(site.created);

export const MassQueryToolingTable: React.FunctionComponent<MassQueryToolingTableProps> = ({
  sites,
  clientsMap,
  selectedTenantIds,
  onToggleSite,
  onToggleAll,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const [sortColumnId, setSortColumnId] = React.useState<string | undefined>(undefined);
  const [sortDirection, setSortDirection] = React.useState<SortDirection | undefined>(undefined);

  const [page, setPage] = React.useState(0);
  const [itemsPerPage, setItemsPerPage] = React.useState<number>(ITEMS_PER_PAGE[0]);

  const handleHeaderClick = (columnId: string) => {
    setSortColumnId((current) => {
      if (current !== columnId) {
        setSortDirection('asc');
        return columnId;
      }

      setSortDirection((currentDirection) => {
        if (currentDirection === 'asc') return 'desc';
        if (currentDirection === 'desc') return undefined;
        return 'asc';
      });

      return columnId;
    });
  };

  const handlePerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPage(0);
    setItemsPerPage(Number(event.target.value));
  };

  const paginatedSites = React.useMemo(() => {
    const start = page * itemsPerPage;
    return sites.slice(start, start + itemsPerPage);
  }, [sites, page, itemsPerPage]);

  const sortedPaginatedSites = React.useMemo(() => {
    if (!sortColumnId || !sortDirection) return paginatedSites;

    const orderSign = sortDirection === 'asc' ? 1 : -1;

    const getComparator = (columnId: string) => {
      switch (columnId) {
        case 'instanceUrl':
          return (a: LearnInstanceItem, b: LearnInstanceItem) =>
            orderSign * (a.Url || '').localeCompare(b.Url || '');
        case 'installationId':
          return (a: LearnInstanceItem, b: LearnInstanceItem) =>
            orderSign * (a.InstallationId || '').localeCompare(b.InstallationId || '');
        case 'clientName':
          return (a: LearnInstanceItem, b: LearnInstanceItem) => {
            const nameA = a.ClientId && clientsMap ? (clientsMap.get(a.ClientId)?.name ?? '') : '';

            const nameB = b.ClientId && clientsMap ? (clientsMap.get(b.ClientId)?.name ?? '') : '';

            return orderSign * nameA.localeCompare(nameB);
          };
        case 'fleetId':
          return (a: LearnInstanceItem, b: LearnInstanceItem) =>
            orderSign * (a.FleetId || '').localeCompare(b.FleetId || '');
        case 'learnVersion':
          return (a: LearnInstanceItem, b: LearnInstanceItem) =>
            orderSign * (a.LearnVersion || '').localeCompare(b.LearnVersion || '');
        default:
          return null;
      }
    };

    const comparator = getComparator(sortColumnId);
    if (!comparator) return paginatedSites;

    return [...paginatedSites].sort(comparator);
  }, [paginatedSites, sortColumnId, sortDirection, clientsMap]);

  const allSelected =
    sites.length > 0 &&
    sites.every((site) => (site.TenantId ? selectedTenantIds.has(site.TenantId) : false));

  const someSelected =
    sites.some((site) => site.TenantId && selectedTenantIds.has(site.TenantId)) && !allSelected;

  return (
    <>
      <TableContainer className={classes.tableContainer}>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell>
                <Tooltip
                  title={t('massQueryTooling.toolTip.select') as string}
                  arrow
                  placement="top-start"
                >
                  <div className={classes.selectHeader}>
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={onToggleAll}
                    />
                  </div>
                </Tooltip>
              </TableCell>
              <SortableTableHeaderCell
                id="mass-query-tooling-header-url"
                tooltip={t('massQueryTooling.toolTip.sorting.url')}
                columnId="instanceUrl"
                onClick={() => handleHeaderClick('instanceUrl')}
              >
                {t('massQueryTooling.tableHeaders.url')}
              </SortableTableHeaderCell>
              <SortableTableHeaderCell
                id="mass-query-tooling-installation-id"
                tooltip={t('massQueryTooling.toolTip.sorting.installationId')}
                columnId="installationId"
                onClick={() => handleHeaderClick('installationId')}
              >
                {t('massQueryTooling.tableHeaders.installationId')}
              </SortableTableHeaderCell>
              <SortableTableHeaderCell
                id="mass-query-tooling-client-name"
                tooltip={t('massQueryTooling.toolTip.sorting.client')}
                columnId="clientName"
                onClick={() => handleHeaderClick('clientName')}
              >
                {t('massQueryTooling.tableHeaders.client')}
              </SortableTableHeaderCell>
              <SortableTableHeaderCell
                id="mass-query-tooling-fleet-id"
                tooltip={t('massQueryTooling.toolTip.sorting.fleetId')}
                columnId="fleetId"
                onClick={() => handleHeaderClick('fleetId')}
              >
                {t('massQueryTooling.tableHeaders.fleetId')}
              </SortableTableHeaderCell>
              <SortableTableHeaderCell
                id="mass-query-tooling-learn-version"
                tooltip={t('massQueryTooling.toolTip.sorting.learnVersion')}
                columnId="learnVersion"
                onClick={() => handleHeaderClick('learnVersion')}
              >
                {t('massQueryTooling.tableHeaders.learnVersion')}
              </SortableTableHeaderCell>
              <TableCell>{t('massQueryTooling.tableHeaders.createdAt')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedPaginatedSites.map((site, index) => (
              <MassQueryToolingRow
                key={`${getRowId(site)}-${index}`}
                site={site}
                clientsMap={clientsMap}
                index={index}
                selected={!!site.TenantId && selectedTenantIds.has(site.TenantId)}
                onToggle={() => onToggleSite(site)}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Pagination
        id="mass-query-tooling-pagination"
        className={classes.pagination}
        page={page}
        count={sites.length}
        itemsPerPage={itemsPerPage}
        onChangePage={setPage}
        previousButtonLabel={t('global.pagination.previousButtonAriaLabel')}
        nextButtonLabel={t('global.pagination.nextButtonAriaLabel')}
        pageSelectLabel={t('global.pagination.pageSelectLabel')}
        pageSelectAriaLabel={({ page, pages }: { page: number; pages: number }) =>
          t('global.pagination.pageSelectAriaLabel', { page, pages })
        }
        ofPages={({ pages }: { pages: number }) => t('global.pagination.ofPages', { pages })}
      />

      <div className={classes.toolbar}>
        <TextField
          select
          className={classes.textField}
          id="mass-query-tooling-items-per-page"
          label={t('global.itemsPerPage')}
          value={itemsPerPage}
          onChange={handlePerPageChange}
          SelectProps={{ native: true }}
          InputLabelProps={{ className: classes.label }}
          data-testid="mass-query-tooling-items-per-page"
        >
          {ITEMS_PER_PAGE.map((perPage) => (
            <option key={perPage} value={perPage}>
              {String(perPage)}
            </option>
          ))}
        </TextField>
      </div>
    </>
  );
};
